import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChessBoard,
  faPenToSquare,
  faPlus,
  faShareNodes,
  faTrash,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { ButtonGroup } from 'react-bootstrap';
import { MNIDashboard } from '@/core/preferences/export';
import { authService } from '@/core/auth/export';
import { Permissions } from '@/core/permissions/export';
import { useMemo } from 'react';
import { MenuList, MenuListItem } from '@/shared/components/export';
import '@/shared/theme/navbar-buttons.scss';
import '@/app/dashboard/components/dashboard-view-controls.scss';

export default function DashboardViewControls({
  onDelete,
  onEdit,
  selectedDashboard,
  onDashboardSelect,
  onDashboardShare,
  onMarkAsFavorite,
  dashboards,
  onDashboardCreate,
}: {
  onDelete: () => void;
  onEdit: () => void;
  onDashboardCreate: () => void;
  onDashboardSelect: (d: MNIDashboard) => void;
  onDashboardShare: (d: MNIDashboard) => void;
  onMarkAsFavorite: (d: MNIDashboard) => void;
  selectedDashboard?: MNIDashboard;
  dashboards: MNIDashboard[];
}) {
  const isSalesRep = authService
    .getCredentials()
    ?.body.permissions.includes(Permissions.BETA_ACCESS); // Sales Reps have beta access
  const allowedDashboards = isSalesRep ? 15 : 5;

  const isSelectedDashboardFavorite = useMemo(() => {
    return selectedDashboard?.favorite ?? false;
  }, [selectedDashboard]);

  const markAsFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedDashboard) {
      onMarkAsFavorite(selectedDashboard);
    }
  };

  const showCreateOrShare = dashboards.length < allowedDashboards || selectedDashboard;

  const items: MenuListItem[] = [
    ...(dashboards.length > 0
      ? dashboards.map(value => ({
          key: value.id,
          label: value.name,
          icon: faChessBoard,
          favorite: value.favorite,
          checked: value.id === selectedDashboard?.id,
          onSelect: () => onDashboardSelect(value),
        }))
      : [{ key: 'none', label: 'No dashboards', disabled: true }]),
    ...(showCreateOrShare
      ? [
          { key: 'divider-1', label: '', divider: true },
          ...(dashboards.length < allowedDashboards
            ? [
                {
                  key: 'create',
                  label: 'Create dashboard',
                  icon: faPlus,
                  onSelect: onDashboardCreate,
                },
              ]
            : []),
          ...(selectedDashboard
            ? [
                {
                  key: 'share',
                  label: 'Share dashboard (link valid 7 days)',
                  icon: faShareNodes,
                  onSelect: () => onDashboardShare(selectedDashboard),
                },
              ]
            : []),
        ]
      : []),
    { key: 'divider-2', label: '', divider: true },
    {
      key: 'delete',
      label: 'Delete current dashboard',
      icon: faTrash,
      danger: true,
      onSelect: onDelete,
    },
  ];

  return (
    <ButtonGroup>
      <MenuList
        trigger={
          <div className="d-flex flex-grow-1 justify-content-between align-items-center">
            <span>{selectedDashboard?.name || '-none-'}</span>
            <FontAwesomeIcon
              onClick={markAsFavorite}
              className="ms-2"
              icon={isSelectedDashboardFavorite ? faStar : faStarRegular}
            />
          </div>
        }
        toggleClassName="btn btn-outline-primary-500 btn-sm dropdown-toggle navbar-btn dashboard-selector-toggle d-flex align-items-center"
        minWidth={260}
        items={items}
        elevated
      />

      <button
        style={{ fontFamily: 'Inter' }}
        className={
          'btn btn-outline-primary-500 btn-sm navbar-btn navbar-btn-square dashboard-selector-edit'
        }
        onClick={onEdit}
      >
        <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '1.15em' }} />
      </button>
    </ButtonGroup>
  );
}
